import {
    Box,
    Typography,
    Paper,
    Button,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { t } from 'i18next';
import { useLearningPosts } from '../../../hooks/useLearning';

const LearningContentStep = ({
    selectedContents = [],
    initialContents = [],
    durationWeeks,
    onAdd,
    onRemove,
    onUpdate,
    onAddInitial,
    onRemoveInitial
}) => {
    const initialPosts = useLearningPosts('initial');
    const scheduledPosts = useLearningPosts('scheduled');

    const isAlreadyAdded = (uuid) => selectedContents.some((c) => c.contentUuid === uuid);

    const isInInitial = (uuid) => initialContents.some((c) => c.contentUuid === uuid);

    const renderPagination = ({ count, paginationModel, setPaginationModel }) =>
        count > paginationModel.pageSize && (
            <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                    count={Math.ceil(count / paginationModel.pageSize)}
                    page={paginationModel.page + 1}
                    onChange={(_, newPage) =>
                        setPaginationModel((prev) => ({
                            ...prev,
                            page: newPage - 1
                        }))
                    }
                />
            </Box>
        );

    const buildWeekOptions = () => {
        return [
            { label: `${t('plans.questionnaires.week')} 1`, value: 1 },
            { label: `${t('plans.questionnaires.week')} 2`, value: 2 },
            { label: `${t('plans.questionnaires.week')} 3`, value: 3 },

            { label: '1 mês (4 semanas)', value: 4 },
            { label: '2 meses (8 semanas)', value: 8 },
            { label: '3 meses (12 semanas)', value: 12 },
            { label: '6 meses (24 semanas)', value: 24 },
            { label: '1 ano (52 semanas)', value: 52 }
        ];
    };

    const weekOptions = buildWeekOptions();

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('plans.steps.learningContents')}
            </Typography>

            {/* CONTEÚDOS INICIAIS */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography fontWeight="bold" gutterBottom>
                    {t('plans.learningContents.initial')}
                </Typography>

                <List dense>
                    {initialPosts.data.map((c) => (
                        <ListItem
                            key={c.uuid}
                            secondaryAction={
                                isInInitial(c.uuid) ? (
                                    <IconButton
                                        color="error"
                                        onClick={() => onRemoveInitial(c.uuid)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                ) : (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={isAlreadyAdded(c.uuid)}
                                        onClick={() =>
                                            onAddInitial({
                                                contentUuid: c.uuid,
                                                contentTitle: c.title
                                            })
                                        }
                                    >
                                        {t('plans.buttons.add')}
                                    </Button>
                                )
                            }
                        >
                            <ListItemText primary={c.title} secondary={c.description} />
                        </ListItem>
                    ))}
                </List>

                {renderPagination(initialPosts)}
            </Paper>

            <Divider sx={{ my: 3 }} />

            {/* CONTEÚDOS DISPONÍVEIS */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography fontWeight="bold" gutterBottom>
                    {t('plans.learningContents.available')}
                </Typography>

                <List dense>
                    {scheduledPosts.data.map((c) => (
                        <ListItem
                            key={c.uuid}
                            secondaryAction={
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={isAlreadyAdded(c.uuid)}
                                    onClick={() =>
                                        onAdd({
                                            contentUuid: c.uuid,
                                            contentTitle: c.title,
                                            assignedWeek: 1
                                        })
                                    }
                                >
                                    {t('plans.buttons.add')}
                                </Button>
                            }
                        >
                            <ListItemText primary={c.title} secondary={c.description} />
                        </ListItem>
                    ))}
                </List>

                {renderPagination(scheduledPosts)}
            </Paper>

            {/* Conteúdos selecionados */}
            {selectedContents.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography fontWeight="bold" gutterBottom>
                        {t('plans.learningContents.selected')}
                    </Typography>

                    <List dense>
                        {selectedContents.map((c) => (
                            <ListItem
                                key={c.contentUuid}
                                secondaryAction={
                                    <IconButton
                                        color="error"
                                        onClick={() => onRemove(c.contentUuid)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                }
                            >
                                <ListItemText primary={c.contentTitle} />

                                {/* Seletor de semana */}
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel>
                                        {t('plans.questionnaires.assignmentWeek')}
                                    </InputLabel>
                                    <Select
                                        value={c.assignedWeek}
                                        label={t('plans.questionnaires.assignmentWeek')}
                                        onChange={(e) =>
                                            onUpdate(c.contentUuid, e.target.value)
                                        }
                                    >
                                        {weekOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}
        </Box>
    );
};

export default LearningContentStep;
